import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const BusinessRestock = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState({});
  const [showConfirm, setShowConfirm] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [settings, setSettings] = useState(null);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printColumns, setPrintColumns] = useState({
    code_produit: true,
    categorie: false,
    stock_actuel: true,
    stock_minimum: true,
    prix_achat: true,
    quantite: true,
    cout_total: true,
    checkbox: true // Colonne case à cocher pour pointer les achats
  });
  const printRef = useRef();

  useEffect(() => {
    loadRestockList();
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await api.get('/business/settings');
      setSettings(res.data);
    } catch (err) {
      console.error('Erreur chargement paramètres:', err);
    }
  };

  const loadRestockList = async () => {
    try {
      setLoading(true);
      const res = await api.get('/business/restock-list');
      setData(res.data);
      
      // Pré-sélectionner tous les produits avec leur quantité suggérée
      const initial = {};
      res.data.products?.forEach(p => {
        initial[p.id] = {
          selected: true,
          quantity: p.quantite_a_commander
        };
      });
      setSelectedProducts(initial);
    } catch (err) {
      console.error('Erreur chargement liste ravitaillement:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleProduct = (productId) => {
    setSelectedProducts(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        selected: !prev[productId]?.selected
      }
    }));
  };

  const updateQuantity = (productId, quantity) => {
    setSelectedProducts(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        quantity: Math.max(0, parseInt(quantity) || 0)
      }
    }));
  };

  const selectAll = () => {
    const updated = {};
    data?.products?.forEach(p => {
      updated[p.id] = {
        selected: true,
        quantity: selectedProducts[p.id]?.quantity || p.quantite_a_commander
      };
    });
    setSelectedProducts(updated);
  };

  const deselectAll = () => {
    const updated = {};
    data?.products?.forEach(p => {
      updated[p.id] = {
        selected: false,
        quantity: selectedProducts[p.id]?.quantity || p.quantite_a_commander
      };
    });
    setSelectedProducts(updated);
  };

  const getSelectedSummary = () => {
    let totalProducts = 0;
    let totalUnits = 0;
    let totalCost = 0;

    data?.products?.forEach(p => {
      if (selectedProducts[p.id]?.selected) {
        totalProducts++;
        const qty = selectedProducts[p.id]?.quantity || 0;
        totalUnits += qty;
        totalCost += qty * (p.prix_achat || 0);
      }
    });

    return { totalProducts, totalUnits, totalCost };
  };

  // Options de colonnes disponibles pour l'impression
  const columnOptions = [
    { key: 'code_produit', label: 'Code produit' },
    { key: 'categorie', label: 'Catégorie' },
    { key: 'stock_actuel', label: 'Stock actuel' },
    { key: 'stock_minimum', label: 'Seuil minimum' },
    { key: 'prix_achat', label: 'Prix d\'achat unitaire' },
    { key: 'quantite', label: 'Quantité à commander' },
    { key: 'cout_total', label: 'Coût total' },
    { key: 'checkbox', label: 'Cases à cocher (pour pointer)' }
  ];

  const togglePrintColumn = (key) => {
    setPrintColumns(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '', 'width=800,height=600');
    printWindow.document.write(`
      <html>
        <head>
          <title>Liste de Ravitaillement</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Segoe UI', Arial, sans-serif; padding: 20px; color: #333; }
            .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #6366f1; }
            .company-info { display: flex; gap: 15px; align-items: flex-start; }
            .company-logo { width: 70px; height: 70px; object-fit: contain; border-radius: 8px; }
            .company-name { font-size: 20px; font-weight: bold; color: #1f2937; margin-bottom: 5px; }
            .company-details { font-size: 12px; color: #6b7280; line-height: 1.6; }
            .doc-title { text-align: right; }
            .doc-title h1 { font-size: 24px; color: #6366f1; margin-bottom: 5px; }
            .doc-title .date { font-size: 12px; color: #6b7280; }
            .summary { background: #f3f4f6; padding: 15px; border-radius: 8px; margin-bottom: 20px; display: flex; justify-content: space-around; text-align: center; }
            .summary-item { }
            .summary-value { font-size: 18px; font-weight: bold; color: #6366f1; }
            .summary-label { font-size: 11px; color: #6b7280; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { background: #6366f1; color: white; padding: 10px 8px; text-align: left; font-size: 11px; text-transform: uppercase; }
            td { padding: 10px 8px; border-bottom: 1px solid #e5e7eb; font-size: 12px; }
            tr:nth-child(even) { background: #f9fafb; }
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .checkbox-cell { width: 30px; }
            .checkbox { width: 14px; height: 14px; border: 2px solid #6366f1; display: inline-block; border-radius: 3px; }
            .footer { margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #6b7280; text-align: center; }
            .total-row { font-weight: bold; background: #eef2ff !important; }
            .product-name { font-weight: 500; }
            .product-code { font-size: 10px; color: #6b7280; }
            @media print {
              body { padding: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
    setShowPrintModal(false);
  };

  // Obtenir les produits sélectionnés pour l'impression
  const getProductsToPrint = () => {
    return data?.products?.filter(p => selectedProducts[p.id]?.selected) || [];
  };

  const handleRestock = async () => {
    setProcessing(true);
    try {
      const productsToRestock = data?.products
        ?.filter(p => selectedProducts[p.id]?.selected && selectedProducts[p.id]?.quantity > 0)
        ?.map(p => ({
          product_id: p.id,
          quantity: selectedProducts[p.id].quantity
        }));

      if (productsToRestock.length === 0) {
        alert('Aucun produit sélectionné');
        setProcessing(false);
        return;
      }

      const result = await api.post('/business/restock', { products: productsToRestock });
      
      setShowConfirm(false);
      loadRestockList(); // Recharger la liste
      
      // Afficher le message de succès avec la référence
      const ref = result.data?.reference || 'N/A';
      const cout = result.data?.total_cout?.toLocaleString() || '0';
      alert(`✅ Ravitaillement effectué avec succès !\n\n📋 Référence: ${ref}\n💰 Décaissement créé: ${cout} XOF\n\nVous pouvez consulter l'historique pour plus de détails.`);
    } catch (err) {
      console.error('Erreur ravitaillement:', err);
      alert('Erreur lors du ravitaillement');
    } finally {
      setProcessing(false);
    }
  };

  const summary = getSelectedSummary();

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Link to="/workspace" className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200">
            ←
          </Link>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-800">🚚 Ravitaillement de stock</h1>
            <p className="text-gray-500 text-sm">Produits à commander pour maintenir le stock au seuil minimum</p>
          </div>
        </div>
        <Link
          to="/workspace/restock/history"
          className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 flex items-center gap-2 text-sm"
        >
          📜 Historique
        </Link>
      </div>

      {/* Résumé */}
      <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl p-4 md:p-6 text-white mb-6">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl md:text-3xl font-bold">{data?.summary?.total_produits || 0}</div>
            <div className="text-xs md:text-sm opacity-80">Produits à ravitailler</div>
          </div>
          <div>
            <div className="text-2xl md:text-3xl font-bold">{data?.summary?.total_unites || 0}</div>
            <div className="text-xs md:text-sm opacity-80">Unités nécessaires</div>
          </div>
          <div>
            <div className="text-2xl md:text-3xl font-bold">{data?.summary?.cout_total?.toLocaleString() || 0}</div>
            <div className="text-xs md:text-sm opacity-80">Coût estimé (XOF)</div>
          </div>
        </div>
      </div>

      {data?.products?.length > 0 ? (
        <>
          {/* Actions */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex gap-2">
              <button
                onClick={selectAll}
                className="px-3 py-1.5 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200"
              >
                Tout sélectionner
              </button>
              <button
                onClick={deselectAll}
                className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Tout désélectionner
              </button>
              <button
                onClick={() => setShowPrintModal(true)}
                disabled={summary.totalProducts === 0}
                className="px-3 py-1.5 text-sm bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 flex items-center gap-1 disabled:opacity-50"
              >
                🖨️ Imprimer la liste
              </button>
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-semibold text-purple-600">{summary.totalProducts}</span> produits sélectionnés
            </div>
          </div>

          {/* Liste des produits */}
          <div className="bg-white rounded-xl shadow overflow-hidden mb-6">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Produit</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Stock actuel</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Seuil</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">À commander</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Coût</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.products.map(product => (
                  <tr 
                    key={product.id} 
                    className={`hover:bg-gray-50 transition ${selectedProducts[product.id]?.selected ? 'bg-purple-50' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedProducts[product.id]?.selected || false}
                          onChange={() => toggleProduct(product.id)}
                          className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                        />
                        <div>
                          <div className="font-medium text-gray-800">{product.nom}</div>
                          <div className="text-xs text-gray-500">{product.code_produit} • {product.categorie}</div>
                        </div>
                      </label>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`font-semibold ${product.stock_actuel === 0 ? 'text-red-600' : 'text-orange-600'}`}>
                        {product.stock_actuel}
                      </span>
                      <span className="text-xs text-gray-500 ml-1">{product.unite}</span>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600">
                      {product.stock_minimum}
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        min="0"
                        value={selectedProducts[product.id]?.quantity || 0}
                        onChange={(e) => updateQuantity(product.id, e.target.value)}
                        disabled={!selectedProducts[product.id]?.selected}
                        className={`w-20 mx-auto block text-center px-2 py-1 border rounded-lg focus:ring-2 focus:ring-purple-500 ${
                          !selectedProducts[product.id]?.selected ? 'bg-gray-100 text-gray-400' : 'bg-white'
                        }`}
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="font-medium text-gray-800">
                        {((selectedProducts[product.id]?.quantity || 0) * (product.prix_achat || 0)).toLocaleString()} XOF
                      </div>
                      <div className="text-xs text-gray-500">
                        {(product.prix_achat || 0).toLocaleString()} XOF/unité
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Barre de validation fixe */}
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 md:ml-64">
            <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-center sm:text-left">
                <div className="text-sm text-gray-600">
                  <span className="font-semibold">{summary.totalProducts}</span> produits • 
                  <span className="font-semibold ml-1">{summary.totalUnits}</span> unités
                </div>
                <div className="text-lg font-bold text-purple-600">
                  Total: {summary.totalCost.toLocaleString()} XOF
                </div>
              </div>
              <button
                onClick={() => setShowConfirm(true)}
                disabled={summary.totalProducts === 0}
                className="px-6 py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
              >
                <span>✓</span>
                Valider le ravitaillement
              </button>
            </div>
          </div>

          {/* Espace pour la barre fixe */}
          <div className="h-24"></div>
        </>
      ) : (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <div className="text-6xl mb-4">✅</div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Stock suffisant !</h3>
          <p className="text-gray-500 mb-4">
            Tous vos produits sont au-dessus du seuil minimum.
          </p>
          <Link
            to="/workspace/products"
            className="text-purple-600 hover:underline"
          >
            Voir tous les produits →
          </Link>
        </div>
      )}

      {/* Modal de confirmation */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">🚚 Confirmer le ravitaillement</h3>
            <p className="text-gray-600 mb-4">
              Vous allez ajouter au stock :
            </p>
            <div className="bg-purple-50 rounded-lg p-4 mb-4">
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">Produits :</span>
                <span className="font-semibold">{summary.totalProducts}</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">Unités :</span>
                <span className="font-semibold">{summary.totalUnits}</span>
              </div>
              <div className="flex justify-between border-t pt-2 mt-2">
                <span className="text-gray-600">Coût estimé :</span>
                <span className="font-bold text-purple-600">{summary.totalCost.toLocaleString()} XOF</span>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200"
              >
                Annuler
              </button>
              <button
                onClick={handleRestock}
                disabled={processing}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {processing ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                    En cours...
                  </>
                ) : (
                  <>
                    <span>✓</span>
                    Confirmer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'options d'impression */}
      {showPrintModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-800 mb-4">🖨️ Imprimer la liste de ravitaillement</h3>
            
            <p className="text-sm text-gray-600 mb-4">
              Sélectionnez les colonnes à afficher sur la feuille :
            </p>
            
            <div className="grid grid-cols-2 gap-3 mb-6">
              {columnOptions.map(option => (
                <label 
                  key={option.key}
                  className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                >
                  <input
                    type="checkbox"
                    checked={printColumns[option.key]}
                    onChange={() => togglePrintColumn(option.key)}
                    className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700">{option.label}</span>
                </label>
              ))}
            </div>

            <div className="bg-indigo-50 rounded-lg p-3 mb-4">
              <p className="text-sm text-indigo-700">
                📋 <strong>{summary.totalProducts}</strong> produits seront imprimés pour un total de <strong>{summary.totalCost.toLocaleString()} XOF</strong>
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowPrintModal(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200"
              >
                Annuler
              </button>
              <button
                onClick={handlePrint}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center justify-center gap-2"
              >
                🖨️ Imprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contenu imprimable (caché) */}
      <div style={{ display: 'none' }}>
        <div ref={printRef}>
          {/* En-tête entreprise */}
          <div className="header">
            <div className="company-info">
              {settings?.logo && (
                <img 
                  src={settings.logo.startsWith('http') ? settings.logo : `${BACKEND_URL}${settings.logo}`}
                  alt="Logo"
                  className="company-logo"
                />
              )}
              <div>
                <div className="company-name">{settings?.nom_entreprise || 'Mon Entreprise'}</div>
                <div className="company-details">
                  {settings?.adresse && <div>{settings.adresse}</div>}
                  {settings?.telephone && <div>📞 {settings.telephone}</div>}
                  {settings?.email && <div>📧 {settings.email}</div>}
                  {settings?.numero_registre && <div>RCCM: {settings.numero_registre}</div>}
                  {settings?.numero_fiscal && <div>NIF: {settings.numero_fiscal}</div>}
                </div>
              </div>
            </div>
            <div className="doc-title">
              <h1>LISTE DE RAVITAILLEMENT</h1>
              <div className="date">Date: {new Date().toLocaleDateString('fr-FR')}</div>
            </div>
          </div>

          {/* Résumé */}
          <div className="summary">
            <div className="summary-item">
              <div className="summary-value">{summary.totalProducts}</div>
              <div className="summary-label">Produits</div>
            </div>
            <div className="summary-item">
              <div className="summary-value">{summary.totalUnits}</div>
              <div className="summary-label">Unités à commander</div>
            </div>
            {printColumns.cout_total && (
              <div className="summary-item">
                <div className="summary-value">{summary.totalCost.toLocaleString()} XOF</div>
                <div className="summary-label">Coût estimé</div>
              </div>
            )}
          </div>

          {/* Tableau des produits */}
          <table>
            <thead>
              <tr>
                {printColumns.checkbox && <th className="checkbox-cell"></th>}
                <th>Produit</th>
                {printColumns.code_produit && <th>Code</th>}
                {printColumns.categorie && <th>Catégorie</th>}
                {printColumns.stock_actuel && <th className="text-center">Stock actuel</th>}
                {printColumns.stock_minimum && <th className="text-center">Seuil</th>}
                {printColumns.quantite && <th className="text-center">Qté à commander</th>}
                {printColumns.prix_achat && <th className="text-right">Prix unitaire</th>}
                {printColumns.cout_total && <th className="text-right">Coût total</th>}
              </tr>
            </thead>
            <tbody>
              {getProductsToPrint().map(product => {
                const qty = selectedProducts[product.id]?.quantity || 0;
                const cost = qty * (product.prix_achat || 0);
                return (
                  <tr key={product.id}>
                    {printColumns.checkbox && (
                      <td className="checkbox-cell">
                        <span className="checkbox"></span>
                      </td>
                    )}
                    <td>
                      <div className="product-name">{product.nom}</div>
                    </td>
                    {printColumns.code_produit && (
                      <td><span className="product-code">{product.code_produit}</span></td>
                    )}
                    {printColumns.categorie && <td>{product.categorie}</td>}
                    {printColumns.stock_actuel && (
                      <td className="text-center">{product.stock_actuel} {product.unite}</td>
                    )}
                    {printColumns.stock_minimum && (
                      <td className="text-center">{product.stock_minimum}</td>
                    )}
                    {printColumns.quantite && (
                      <td className="text-center"><strong>{qty}</strong> {product.unite}</td>
                    )}
                    {printColumns.prix_achat && (
                      <td className="text-right">{(product.prix_achat || 0).toLocaleString()} XOF</td>
                    )}
                    {printColumns.cout_total && (
                      <td className="text-right">{cost.toLocaleString()} XOF</td>
                    )}
                  </tr>
                );
              })}
              {/* Ligne total */}
              {printColumns.cout_total && (
                <tr className="total-row">
                  {printColumns.checkbox && <td></td>}
                  <td colSpan={
                    1 + 
                    (printColumns.code_produit ? 1 : 0) + 
                    (printColumns.categorie ? 1 : 0) + 
                    (printColumns.stock_actuel ? 1 : 0) + 
                    (printColumns.stock_minimum ? 1 : 0) + 
                    (printColumns.quantite ? 1 : 0) + 
                    (printColumns.prix_achat ? 1 : 0)
                  } className="text-right">TOTAL</td>
                  <td className="text-right">{summary.totalCost.toLocaleString()} XOF</td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pied de page */}
          <div className="footer">
            <p>Document généré le {new Date().toLocaleDateString('fr-FR')} à {new Date().toLocaleTimeString('fr-FR')}</p>
            {settings?.nom_entreprise && <p>© {new Date().getFullYear()} {settings.nom_entreprise}</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessRestock;
